import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SignOut, User, Users, Bell, Plug, Info, Trash, Sparkle, PhoneCall, ShieldCheck, CaretRight, CalendarBlank, Copy, UsersThree, Airplane, Pause, Play, Key, PencilSimple, PaperPlaneTilt, WhatsappLogo, Sliders } from "@phosphor-icons/react";
import UserTeamsDialog from "@/components/UserTeamsDialog";
import LeaveBalanceDialog from "@/components/LeaveBalanceDialog";
import UserPermissionsDialog from "@/components/UserPermissionsDialog";
import PasswordResetDialog from "@/components/PasswordResetDialog";
import EditUserDialog from "@/components/EditUserDialog";
import ReassignWorkloadDialog from "@/components/ReassignWorkloadDialog";
import SendInvitationDialog from "@/components/SendInvitationDialog";
import TeamsConfigCard from "@/components/TeamsConfigCard";
import PushSettingsCard from "@/components/PushSettingsCard";
import InstallAppCard from "@/components/InstallAppCard";
import DepotSettingsCard from "@/components/DepotSettingsCard";
import PayrollZonesCard from "@/components/PayrollZonesCard";
import GoogleAccountCard from "@/components/GoogleAccountCard";
import CallMeBotCard from "@/components/CallMeBotCard";
import HomeAddressCard from "@/components/HomeAddressCard";
import AdminCallMeBotDialog from "@/components/AdminCallMeBotDialog";
import DashboardPrefsDialog from "@/components/DashboardPrefsDialog";
import TrackingConsentCard from "@/components/TrackingConsentCard";

const ROLE_LABEL = { admin: "Gérant", compta: "Compta", associate: "Associé", technician: "Technicien", external: "Externe" };
const ROLE_COLOR = { admin: "#005CFF", compta: "#8B5CF6", associate: "#10B981", technician: "#F59E0B", external: "#94A3B8" };

export default function Reglages() {
  const { user, logout, isAdmin } = useAuth();
  const [team, setTeam] = useState([]);
  const [teamsDlgUser, setTeamsDlgUser] = useState(null);
  const [balanceDlgUser, setBalanceDlgUser] = useState(null);
  const [permDlgUser, setPermDlgUser] = useState(null);
  const isAdminOrCompta = user?.role === "admin" || user?.role === "compta";
  const isAssociate = user?.role === "associate";
  const canOpenConsole = isAdminOrCompta || isAssociate;
  const [pwResetUser, setPwResetUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [reassignUser, setReassignUser] = useState(null);
  const [inviteUser, setInviteUser] = useState(null);
  const [callmebotUser, setCallmebotUser] = useState(null);
  const [dashPrefsOpen, setDashPrefsOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/users");
    setTeam(r.data);
  };

  const del = async (u) => {
    if (!window.confirm(`⚠️ Supprimer DÉFINITIVEMENT ${u.name} ?\nSes chantiers/notes historiques garderont son nom mais il ne pourra plus être sélectionné nulle part.\n\nAstuce : préfère "Mettre en pause" pour désactiver sans casser l'historique.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (u) => {
    const willDeactivate = u.is_active !== false;
    if (willDeactivate) {
      // Open reassign dialog first — it fetches the workload and lets the admin transfer
      // future interventions + active chantiers to another tech before pausing.
      setReassignUser(u);
      return;
    }
    try {
      await api.patch(`/users/${u.id}`, { is_active: true });
      toast.success(`${u.name} réactivé`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 space-y-5">
      {/* Profile */}
      <Section title="Profil">
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#005CFF] to-[#0047CC] flex items-center justify-center text-lg font-bold shadow-[0_0_20px_rgba(0,92,255,0.4)]">
            {(user?.name || "?").split(" ").map(s => s[0]).join("").slice(0,2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg truncate" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-xs text-white/50 flex items-center gap-1.5"><PhoneCall size={12} /> {user?.phone}</div>
            <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: ROLE_COLOR[user?.role] || "#005CFF" }}>{ROLE_LABEL[user?.role]}</div>
          </div>
        </div>
        <Button onClick={logout} variant="ghost" className="w-full mt-2 border border-white/10 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-12 rounded-2xl" data-testid="logout-btn">
          <SignOut size={18} /> Déconnexion
        </Button>
        <div className="mt-3">
          <GoogleAccountCard />
        </div>
        <CallMeBotCard />
        <HomeAddressCard />
        <button
          onClick={() => setDashPrefsOpen(true)}
          className="mt-3 w-full flex items-center gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-3 active:scale-[0.99] hover:border-white/20 transition-colors"
          data-testid="dash-prefs-open"
        >
          <div className="w-9 h-9 rounded-xl bg-[#7CB2FF]/15 border border-[#7CB2FF]/25 flex items-center justify-center">
            <Sliders size={16} weight="duotone" className="text-[#7CB2FF]" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold">Blocs de l&apos;accueil</div>
            <div className="text-[10px] text-white/50">Coche / décoche : météo, chiffres clés, chantiers récents…</div>
          </div>
          <CaretRight size={12} className="text-white/40" />
        </button>
        <div className="mt-3">
          <TrackingConsentCard />
        </div>
      </Section>

      {/* Admin console — Gérant + Associé (with reduced scope for the latter) */}
      {canOpenConsole && (
        <button
          onClick={() => nav("/admin")}
          className="w-full text-left rounded-2xl bg-gradient-to-br from-[#005CFF]/20 to-[#005CFF]/5 border border-[#005CFF]/30 p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
          data-testid="admin-console-link"
        >
          <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
            <ShieldCheck size={22} weight="fill" color="white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">{isAssociate ? "Console associé" : "Console gérant"}</div>
            <div className="text-xs text-white/60">
              {isAssociate
                ? "Fiches types · Modèles rapport · Outillage · Catalogue"
                : "Assistant IA · Fiches types · À passer en devis"}
            </div>
          </div>
          <CaretRight size={16} className="text-white/40" />
        </button>
      )}

      {/* Congés */}
      <button
        onClick={() => nav("/rh")}
        className="w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3 hover:border-white/20 active:scale-[0.99] transition-transform"
        data-testid="leave-link"
      >
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <CalendarBlank size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">Congés & Absences</div>
          <div className="text-[10px] text-white/50">{isAdmin ? "Valider les demandes de l'équipe" : "Déposer une demande de congé"}</div>
        </div>
        <CaretRight size={14} className="text-white/30" />
      </button>

      {/* Team */}
      {isAdmin && (
        <Section title="Équipe" trailing={<Users size={16} className="text-white/40" />}>
          <TeamsConfigCard />
          <div className="space-y-2 mt-3">
            {team.map((u) => {
              const isInactive = u.is_active === false;
              return (
              <div key={u.id} className={`rounded-2xl border p-3 flex items-center gap-3 transition-colors ${isInactive ? "bg-white/[0.01] border-white/5 opacity-55" : "bg-white/[0.03] border-white/10"}`} data-testid={`team-row-${u.id}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: (u.color || "#005CFF") + "33", color: u.color || "#005CFF" }}>
                  {(u.name || "?").split(" ").map(s => s[0]).join("").slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {u.name}
                    {isInactive && <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400" data-testid={`inactive-badge-${u.id}`}>En pause</span>}
                  </div>
                  <div className="text-xs text-white/50 truncate">{u.phone}</div>
                  {u.teams && u.teams.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.teams.map((t) => (
                        <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#005CFF]/20 text-[#7CB2FF] uppercase tracking-widest">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: ROLE_COLOR[u.role] }}>{ROLE_LABEL[u.role]}</div>
                {user?.role === "admin" && (
                  <button onClick={() => setEditUser(u)} className="text-white/40 hover:text-[#005CFF] p-1" title="Modifier (nom, tél, couleur)" data-testid={`edit-user-${u.id}`}>
                    <PencilSimple size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && (
                  <button onClick={() => setPermDlgUser(u)} className="text-white/40 hover:text-[#7CB2FF] p-1" title="Permissions" data-testid={`edit-perms-${u.id}`}>
                    <ShieldCheck size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && (
                  <button onClick={() => setBalanceDlgUser(u)} className="text-white/40 hover:text-emerald-400 p-1" title="Solde congés" data-testid={`edit-balance-${u.id}`}>
                    <Airplane size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && (
                  <button onClick={() => setTeamsDlgUser(u)} className="text-white/40 hover:text-[#005CFF] p-1" title="Sous-équipes" data-testid={`edit-teams-${u.id}`}>
                    <UsersThree size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && (
                  <button onClick={() => setPwResetUser(u)} className="text-white/40 hover:text-amber-400 p-1" title="Générer nouveau mot de passe" data-testid={`reset-pw-${u.id}`}>
                    <Key size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && (
                  <button
                    onClick={() => setCallmebotUser(u)}
                    className={`p-1 ${u.callmebot_apikey ? "text-emerald-400 hover:text-emerald-300" : "text-white/40 hover:text-emerald-400"}`}
                    title={u.callmebot_apikey ? "CallMeBot configuré — modifier" : "Configurer CallMeBot WhatsApp"}
                    data-testid={`admin-callmebot-btn-${u.id}`}
                  >
                    <WhatsappLogo size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && u.id !== user.id && (
                  <button onClick={() => setInviteUser(u)} className="text-white/40 hover:text-[#7CB2FF] p-1" title="Envoyer une invitation par SMS" data-testid={`invite-user-${u.id}`}>
                    <PaperPlaneTilt size={16} weight="duotone" />
                  </button>
                )}
                {user?.role === "admin" && u.id !== user.id && (
                  <button onClick={() => toggleActive(u)} className={`p-1 ${isInactive ? "text-emerald-400 hover:text-emerald-300" : "text-white/40 hover:text-amber-400"}`} title={isInactive ? "Réactiver" : "Mettre en pause"} data-testid={`toggle-active-${u.id}`}>
                    {isInactive ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
                  </button>
                )}
                {user?.role === "admin" && u.id !== user.id && (
                  <button onClick={() => del(u)} className="text-white/40 hover:text-red-400 p-1" title="Supprimer définitivement" data-testid={`delete-user-${u.id}`}><Trash size={14} /></button>
                )}
              </div>
              );
            })}
          </div>
          <div className="text-xs text-white/40 mt-2">Utilisez le bouton + pour ajouter un équipier.</div>
          <UserTeamsDialog open={!!teamsDlgUser} onOpenChange={(o) => !o && setTeamsDlgUser(null)} user={teamsDlgUser} onSaved={load} />
          <LeaveBalanceDialog open={!!balanceDlgUser} onOpenChange={(o) => !o && setBalanceDlgUser(null)} user={balanceDlgUser} onSaved={load} />
          <UserPermissionsDialog open={!!permDlgUser} onOpenChange={(o) => !o && setPermDlgUser(null)} user={permDlgUser} onSaved={load} />
          <PasswordResetDialog open={!!pwResetUser} onOpenChange={(o) => !o && setPwResetUser(null)} user={pwResetUser} />
          <EditUserDialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)} user={editUser} onSaved={load} />
          <SendInvitationDialog open={!!inviteUser} onOpenChange={(o) => !o && setInviteUser(null)} user={inviteUser} />
          <AdminCallMeBotDialog open={!!callmebotUser} onOpenChange={(o) => !o && setCallmebotUser(null)} user={callmebotUser} onSaved={load} />
          <DashboardPrefsDialog open={dashPrefsOpen} onOpenChange={setDashPrefsOpen} />
          <ReassignWorkloadDialog
            open={!!reassignUser}
            onOpenChange={(o) => !o && setReassignUser(null)}
            sourceUser={reassignUser}
            team={team}
            pauseAfter={true}
            onDone={load}
          />
        </Section>
      )}

      {/* Calendar sync (personal iCal feed) */}
      <CalendarSyncCard />

      {/* Integrations */}
      <Section title="Intégrations" trailing={<Plug size={16} className="text-white/40" />}>
        <div className="space-y-2">
          <IntegrationRow name="Assistant IA (Claude)" state="active" hint="Streaming, historique" onOpen={() => nav("/ia")} testid="int-ai" />
          <IntegrationRow name="Emergent Email (Resend)" state="active" hint="Envoi PDF client" testid="int-resend" />
          <IntegrationRow name="Object Storage (photos + docs)" state="active" hint="Photos, PDFs, signatures" testid="int-storage" />
          <IntegrationRow name="Siri Shortcuts" state="active" hint="Créer chantier à la voix" onOpen={isAdmin ? () => nav("/admin?tab=siri") : undefined} testid="int-siri" />
          <IntegrationRow name="Gmail — Envoi rapport" state="active" hint="Bouton « Envoyer » sur chaque chantier" testid="int-gmail" />
          <IntegrationRow name="Axonaut (devis)" state="config" hint="Clé API à saisir" onOpen={isAdmin ? () => nav("/admin?tab=axonaut") : undefined} testid="int-axonaut" />
          <IntegrationRow name="Twilio SMS" state="config" hint="SID + token à saisir" onOpen={isAdmin ? () => nav("/admin?tab=twilio") : undefined} testid="int-twilio" />
          <IntegrationRow name="n8n / Zapier webhooks" state={isAdmin ? "active" : "config"} hint="Envoi événements sortants signés HMAC" onOpen={isAdmin ? () => nav("/admin?tab=webhooks") : undefined} testid="int-n8n" />
          <IntegrationRow name="MCP (Claude Desktop)" state={isAdmin ? "active" : "config"} hint="Voix / agent IA autonome" onOpen={isAdmin ? () => nav("/admin?tab=mcp") : undefined} testid="int-mcp" />
        </div>
      </Section>

      {/* Notifications */}
      <Section title="Notifications" trailing={<Bell size={16} className="text-white/40" />}>
        <InstallAppCard />
        <PushSettingsCard />
        <NotifRow label="Nouvelles tâches" />
        <NotifRow label="Alertes véhicules / matériel" />
        <NotifRow label="Congés en attente" />
      </Section>

      {/* Dépôt (admin/manager only) */}
      {isAdmin && (
        <Section title="Dépôt & horaires" trailing={<Plug size={16} className="text-white/40" />}>
          <DepotSettingsCard />
        </Section>
      )}

      {/* Paniers & Trajets — zones URSSAF (compta + admin) */}
      {(isAdminOrCompta) && (
        <Section title="Zones paniers & trajets" trailing={<Sliders size={16} className="text-white/40" />}>
          <PayrollZonesCard />
        </Section>
      )}

      {/* About */}
      <Section title="À propos" trailing={<Info size={16} className="text-white/40" />}>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2 text-sm">
          <Row k="Application" v="BKflow" />
          <Row k="Version" v="0.1.0 · MVP" />
          <Row k="Suite" v={<span className="text-white/50">Chantier · <span className="opacity-40">Vitrine</span> · <span className="opacity-40">Vidéo</span></span>} />
          <Row k="Contact" v={user?.email || "—"} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children, trailing }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">{title}</div>
        {trailing}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function IntegrationRow({ name, state, hint, onOpen, testid }) {
  const meta = {
    active: { label: "Actif", color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/30" },
    config: { label: "À configurer", color: "text-amber-400", bg: "bg-amber-500/15 border-amber-500/30" },
    soon: { label: "Bientôt", color: "text-white/40", bg: "bg-white/5 border-white/10" },
  }[state] || { label: state, color: "text-white/40", bg: "bg-white/5 border-white/10" };
  const Wrapper = onOpen ? "button" : "div";
  return (
    <Wrapper
      onClick={onOpen}
      className={`w-full text-left rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center justify-between gap-3 ${onOpen ? "hover:border-white/20 active:scale-[0.99] transition-transform" : ""}`}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="font-medium text-sm truncate">{name}</div>
        {hint && <div className="text-[10px] text-white/40 truncate">{hint}</div>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${meta.bg} ${meta.color}`}>{meta.label}</span>
        {onOpen && <CaretRight size={14} className="text-white/30" />}
      </div>
    </Wrapper>
  );
}

function CalendarSyncCard() {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const feedUrl = token ? `${BACKEND_URL}/api/calendar-feed/${token}.ics` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/calendar-feed/token");
      setToken(r.data.token);
      toast.success("Lien de synchronisation généré");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Révoquer le lien ? Vos calendriers abonnés cesseront d'être mis à jour.")) return;
    try { await api.delete("/calendar-feed/token"); setToken(null); toast.success("Lien révoqué"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const copy = () => {
    navigator.clipboard.writeText(feedUrl);
    toast.success("Copié dans le presse-papier");
  };

  return (
    <Section title="Synchro calendrier" trailing={<CalendarBlank size={16} className="text-white/40" />}>
      <div className="rounded-2xl bg-gradient-to-br from-purple-500/10 to-[#005CFF]/5 border border-purple-500/20 p-4 space-y-3">
        <div className="text-xs text-white/70">
          Abonnez votre agenda <b>Apple Calendar</b>, <b>Google Calendar</b> ou <b>Outlook</b> à vos interventions BKPRO. Le calendrier se met à jour automatiquement.
        </div>
        {!token ? (
          <Button onClick={generate} disabled={busy} className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl" data-testid="ical-generate">
            <CalendarBlank size={16} weight="bold" /> {busy ? "…" : "Générer mon lien de synchro"}
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="rounded-xl bg-black/40 border border-white/10 p-2 flex items-center gap-2">
              <div className="flex-1 min-w-0 text-[11px] font-mono truncate text-white/80" data-testid="ical-url">{feedUrl}</div>
              <button onClick={copy} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="ical-copy"><Copy size={14} className="text-[#005CFF]" /></button>
            </div>
            <div className="text-[10px] text-white/50 leading-relaxed">
              <b>iPhone / iPad</b> : Réglages → Calendrier → Comptes → Ajouter → Autre → « Ajouter un abonnement calendrier » → coller le lien.<br/>
              <b>Google Calendar</b> : Paramètres → « Ajouter un calendrier » → À partir de l&apos;URL → coller le lien.<br/>
              <b>Outlook</b> : Ajouter calendrier → « À partir d&apos;internet » → coller le lien.
            </div>
            <Button onClick={revoke} variant="ghost" className="w-full h-9 border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid="ical-revoke">
              <Trash size={12} /> Révoquer le lien
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
}

function NotifRow({ label }) {
  const [on, setOn] = useState(true);
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center justify-between">
      <div className="text-sm">{label}</div>
      <Switch checked={on} onCheckedChange={setOn} className="data-[state=checked]:bg-[#005CFF]" />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-white/50 text-xs uppercase tracking-widest">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}
